/**
 * Garage Request Detail View
 * Full-page dedicated view for a single request with conversation thread
 */

import React, { useState, useEffect } from 'react';
import { ThumbsUp, Send, Clock, User, MessageCircle, AlertCircle, TrendingUp } from 'lucide-react';
import garageAPI from '../../../services/garage/api';
import config from '../../../config/environment';
import RequestInfoModal from '../modals/RequestInfoModal';
import RouteToITModal from '../modals/RouteToITModal';
import EscalateToDotModal, { getEscalateJob } from '../modals/EscalateToDotModal';
import PublishGemModal from '../modals/PublishGemModal';
import StageTransitionModal from '../modals/StageTransitionModal';
import GarageLoadingScreen, { GarageLoadingRegion } from '../components/GarageLoadingScreen';
import RequestActivityFeed from '../components/RequestActivityFeed';
import ConfirmModal from '../modals/ConfirmModal';
import AlertModal from '../modals/AlertModal';
import RequestAttachmentPicker from '../components/RequestAttachmentPicker';
import RequestWorkflowPanel, { needsItScanPanel } from '../components/RequestWorkflowPanel';
import { formatTimestamp, parseTimestamp } from '../../../utils/dateFormatter';
import { stageLabel } from '../../../utils/ideaStatusTransitions';
import {
  canManageRequests as roleCanManageRequests,
  getAdminPersonaScopes,
  isPersonaInAdminScope,
} from '../constants/roleAccess';

// Component to handle async timestamp formatting
const TimestampDisplay = ({ timestamp, userEmail, showTime = false, showTimezone = false }) => {
  const [formatted, setFormatted] = useState('');

  useEffect(() => {
    const format = async () => {
      const result = await formatTimestamp(timestamp, {
        showTime,
        showTimezone,
        showRelative: true,
        userEmail
      });
      setFormatted(result);
    };
    if (timestamp) {
      format();
    }
  }, [timestamp, userEmail, showTime, showTimezone]);

  return formatted || parseTimestamp(timestamp).toLocaleDateString();
};

const GarageRequestDetailView = ({
  requestId,
  userEmail,
  userName,
  isAdmin,
  userRole,
  accessPersona,
  accessPersonas = [],
  onBack,
  backLabel = 'Back to My Requests',
}) => {
  const [request, setRequest] = useState(null);
  const [itReviewPayload, setItReviewPayload] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusTransitioning, setStatusTransitioning] = useState(false);
  const [transitionMessage, setTransitionMessage] = useState('Updating status…');
  const [stageTransitionModal, setStageTransitionModal] = useState({
    isOpen: false,
    toStatus: null,
    title: '',
    confirmLabel: 'Confirm',
    variant: 'default',
  });
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const [downloadingAttachment, setDownloadingAttachment] = useState(null);
  const [votesCount, setVotesCount] = useState(0);
  const [hasVoted, setHasVoted] = useState(false);
  const [submittedDate, setSubmittedDate] = useState('');

  const adminPersonaScopes = getAdminPersonaScopes(
    userRole,
    accessPersonas,
    accessPersona
  );
  const canManageRequests = (
    isAdmin || roleCanManageRequests(userRole, adminPersonaScopes)
  ) && (
    adminPersonaScopes.length === 0
    || isPersonaInAdminScope(userRole, request?.primaryPersona, adminPersonaScopes)
  );
  const canEscalateToDot = canManageRequests && (
    (isAdmin && adminPersonaScopes.length === 0) ||
    ['admin', 'analyst_reviewer', 'persona_admin'].includes(String(userRole || '').toLowerCase())
  );

  // Modal states for admin/reviewer actions
  const [requestInfoModalOpen, setRequestInfoModalOpen] = useState(false);
  const [routeToITModalOpen, setRouteToITModalOpen] = useState(false);
  const [escalateDotModalOpen, setEscalateDotModalOpen] = useState(false);
  const [pendingDotReview, setPendingDotReview] = useState(false);
  const [publishGemModalOpen, setPublishGemModalOpen] = useState(false);

  // Confirm and Alert modals
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null, variant: 'default' });
  const [alertModal, setAlertModal] = useState({ isOpen: false, title: '', message: '', variant: 'info' });
  /** Requestor: jump to IT Initiated to supply repo/token */
  const [focusItInitiated, setFocusItInitiated] = useState(false);

  useEffect(() => {
    if (!requestId) return;
    setRequest(null);
    setItReviewPayload(null);
    setLoading(true);
    loadRequestDetails({ showLoader: true });
    loadActivity();
    loadAttachments();
  }, [requestId]);

  const loadRequestDetails = async ({ showLoader = false } = {}) => {
    try {
      if (showLoader) setLoading(true);
      const data = await garageAPI.getIdea(requestId);

      // Fetch IT review before committing request state when the IT panel will show,
      // so we never paint Description / empty stages and then swap in real IT data.
      let nextItPayload = null;
      if (needsItScanPanel(data)) {
        try {
          nextItPayload = await garageAPI.getItReview(requestId);
        } catch (itErr) {
          console.error('Failed to load IT review:', itErr);
          nextItPayload = null;
        }
      }

      setRequest(data);
      setItReviewPayload(nextItPayload);
      setVotesCount(data.votesCount || 0);
      setHasVoted(data.hasVoted || false);
      const stillNeedsInput = Boolean(
        data.itInputRequired || nextItPayload?.itReview?.itInputRequired
      );
      if (!stillNeedsInput) setFocusItInitiated(false);

      if (data.createdAt) {
        const formatted = await formatTimestamp(data.createdAt, {
          showTime: true,
          showTimezone: true,
          userEmail: userEmail
        });
        setSubmittedDate(formatted);
      }
    } catch (error) {
      console.error('Failed to load request details:', error);
      setRequest(null);
      setItReviewPayload(null);
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  const loadActivity = async () => {
    try {
      const data = await garageAPI.getActivity(requestId);
      setActivity(data || []);
    } catch (error) {
      console.error('Failed to load activity:', error);
      setActivity([]);
    }
  };

  const loadAttachments = async () => {
    try {
      const data = await garageAPI.getIdeaAttachments(requestId);
      setAttachments(data || []);
    } catch (error) {
      console.error('Failed to load attachments:', error);
      setAttachments([]);
    }
  };

  const handleVote = async () => {
    try {
      await garageAPI.voteIdea(requestId, userEmail);
      setHasVoted(!hasVoted);
      setVotesCount(hasVoted ? votesCount - 1 : votesCount + 1);
    } catch (error) {
      console.error('Failed to vote:', error);
    }
  };

  const handlePostComment = async () => {
    if (!newComment.trim() && attachmentFiles.length === 0) return;

    const isRequesterNeedsInfoReply =
      ['needs_info', 'needs-info'].includes((request?.status || '').toLowerCase()) &&
      (request?.requesterEmail || '').toLowerCase() === (userEmail || '').toLowerCase();
    const generatedAttachmentReply =
      !newComment.trim() && attachmentFiles.length > 0 && isRequesterNeedsInfoReply;
    const commentText = generatedAttachmentReply
      ? 'Attached the requested information.'
      : newComment.trim();

    let filesAdded = false;
    try {
      setSubmittingComment(true);
      if (attachmentFiles.length > 0) {
        await garageAPI.uploadIdeaAttachments(requestId, attachmentFiles);
        filesAdded = true;
        setAttachmentFiles([]);
        await loadAttachments();
      }
      if (commentText) {
        await garageAPI.createComment({
          ideaId: requestId,
          text: commentText,
          authorEmail: userEmail,
          authorName: userName,
          commentType: isRequesterNeedsInfoReply ? 'info_response' : 'comment'
        });
        setNewComment('');
      }
      await loadActivity();
      // Reload request to check for status change
      await loadRequestDetails();
    } catch (error) {
      console.error('Failed to post comment:', error);
      if (filesAdded) {
        await loadActivity();
      }
      setAlertModal({
        isOpen: true,
        title: filesAdded ? 'Files added; comment failed' : 'Unable to add update',
        message: filesAdded
          ? (
            generatedAttachmentReply
              ? 'The files were attached, but the request could not be returned to review. Please add a short comment to retry.'
              : 'The files were attached, but the comment could not be posted. Your comment is still here so you can retry it.'
          )
          : (error?.response?.data?.detail || 'The comment or files could not be added. Please try again.'),
        variant: 'error'
      });
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      setDownloadingAttachment(attachment.id);
      const blob = await garageAPI.downloadIdeaAttachment(requestId, attachment.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download attachment:', error);
      setAlertModal({
        isOpen: true,
        title: 'Download failed',
        message: error?.response?.data?.detail || 'This file could not be downloaded.',
        variant: 'error'
      });
    } finally {
      setDownloadingAttachment(null);
    }
  };

  const handleDeleteAttachment = (attachment) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete attachment',
      message: `Delete “${attachment.filename}”? This cannot be undone.`,
      variant: 'danger',
      onConfirm: async () => {
        try {
          await garageAPI.deleteIdeaAttachment(requestId, attachment.id);
          await Promise.all([loadAttachments(), loadActivity()]);
        } catch (error) {
          console.error('Failed to delete attachment:', error);
          setAlertModal({
            isOpen: true,
            title: 'Delete failed',
            message: error?.response?.data?.detail || 'This attachment could not be deleted.',
            variant: 'error'
          });
        } finally {
          setConfirmModal((current) => ({ ...current, isOpen: false }));
        }
      }
    });
  };

  const runStatusTransition = async (newStatus, message) => {
    setTransitionMessage(message || `Moving to ${stageLabel(newStatus)}…`);
    setStatusTransitioning(true);
    try {
      await garageAPI.updateIdea(requestId, { status: newStatus });
      await Promise.all([
        loadRequestDetails({ showLoader: false }),
        loadActivity(),
      ]);
    } catch (error) {
      console.error('Failed to update status:', error);
      const detail = error?.response?.data?.detail;
      setAlertModal({
        isOpen: true,
        title: 'Error',
        message: (typeof detail === 'string' && detail)
          || 'Failed to update status. Please try again.',
        variant: 'error',
      });
      await Promise.all([
        loadRequestDetails({ showLoader: false }),
        loadActivity(),
      ]);
    } finally {
      setStatusTransitioning(false);
    }
  };

  // Admin action handlers — open comment modal, then apply status
  const handleStatusChange = (newStatus) => {
    const isReject = newStatus === 'rejected';
    setStageTransitionModal({
      isOpen: true,
      toStatus: newStatus,
      title: isReject
        ? 'Reject request'
        : `Move to ${stageLabel(newStatus)}`,
      confirmLabel: isReject ? 'Reject' : `Move to ${stageLabel(newStatus)}`,
      variant: isReject ? 'danger' : 'default',
    });
  };

  const handleStageTransitionConfirm = async ({ toStatus }) => {
    const label =
      toStatus === 'rejected'
        ? 'Rejecting request…'
        : `Moving to ${stageLabel(toStatus)}…`;
    await runStatusTransition(toStatus, label);
  };

  const refreshAfterAction = async (message = 'Updating…') => {
    setTransitionMessage(message);
    setStatusTransitioning(true);
    try {
      await Promise.all([
        loadRequestDetails({ showLoader: false }),
        loadActivity(),
      ]);
    } finally {
      setStatusTransitioning(false);
    }
  };

  const handleRouteToIT = () => {
    setRouteToITModalOpen(true);
  };

  const handleEscalateToDot = () => {
    setEscalateDotModalOpen(true);
  };

  const handleRequestInfo = () => {
    setRequestInfoModalOpen(true);
  };

  const canPublishGem =
    config.PUBLISH_GEM_ENABLED &&
    canManageRequests &&
    (request?.appType || '').toLowerCase() === 'gem';
  const isGemPublished =
    (request?.status || '').toLowerCase() === 'published' && Boolean(request?.existingToolId);

  const handlePublishGem = () => {
    setPublishGemModalOpen(true);
  };

  useEffect(() => {
    if (!pendingDotReview || !requestId) return undefined;
    const timer = setInterval(() => {
      const job = getEscalateJob(requestId);
      if (job?.status === 'ready') {
        clearInterval(timer);
        setPendingDotReview(false);
        setEscalateDotModalOpen(true);
        setAlertModal({
          isOpen: true,
          title: 'DOT draft ready',
          message: 'AI Intake Assist finished. Review and submit the draft to IT BizOps.',
          variant: 'info',
        });
      } else if (job?.status === 'done') {
        clearInterval(timer);
        setPendingDotReview(false);
        loadRequestDetails();
        loadActivity();
      }
    }, 2000);
    return () => clearInterval(timer);
  }, [pendingDotReview, requestId]);

  const getStatusColor = (status) => {
    const colors = {
      'submitted': '#94a3b8',
      'under-review': '#3b82f6',
      'under_review': '#3b82f6',
      'needs-info': '#f59e0b',
      'needs_info': '#f59e0b',
      'in-progress': '#8b5cf6',
      'in_progress': '#8b5cf6',
      'it-review': '#06b6d4',
      'it_review': '#06b6d4',
      'routed-to-it': '#06b6d4',
      'escalated_to_it_bizops': '#c2410c',
      'optimization': '#10b981',
      'deploying': '#ec4899',
      'published': '#22c55e',
      'declined': '#ef4444',
      'rejected': '#DC2626'
    };
    return colors[status] || '#94a3b8';
  };

  // Status options for admin dropdown
  const statusOptions = [
    { value: 'submitted', label: 'Submitted' },
    { value: 'under_review', label: 'Under review' },
    { value: 'in_progress', label: 'In progress' },
    { value: 'optimization', label: 'Optimization' },
    { value: 'escalated_to_it_bizops', label: 'Escalated to IT Business Operations' },
    { value: 'it_review', label: 'IT review' },
    { value: 'deploying', label: 'Deploying' },
    { value: 'published', label: 'Published' },
    { value: 'needs_info', label: 'Needs info' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'on_hold', label: 'On hold' }
  ];

  const getStatusLabel = (status) => {
    const match = statusOptions.find((opt) => opt.value === status);
    if (match) return match.label;
    return (status || 'pending').replace(/[_-]/g, ' ');
  };

  const formatDate = async (dateString) => {
    if (!dateString) return '';

    // Use timezone-aware formatting with Workday fallback
    return await formatTimestamp(dateString, {
      showTime: false,
      showRelative: true,
      userEmail: userEmail
    });
  };

  // Helper: Format app type label
  const getAppTypeLabel = (appType) => {
    if (appType === 'gem') return 'Gem';
    if (appType === 'app') return 'App';
    return null;
  };

  if (loading) {
    return (
      <div className="garage-content">
        <GarageLoadingScreen
          message={
            needsItScanPanel(request) ? 'Loading IT review…' : 'Loading request details…'
          }
        />
      </div>
    );
  }

  if (!request) {
    return (
      <div className="garage-content">
        <div className="garage-empty-state">
          <AlertCircle size={48} />
          <h3>Request not found</h3>
          <p>The request you're looking for doesn't exist or has been removed.</p>
          {onBack && (
            <button className="garage-btn garage-btn-primary" onClick={onBack}>
              {backLabel}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`garage-content request-detail-page ${request.status === 'rejected' ? 'rejected' : ''}`}>
      {/* Request Header */}
      <div className="request-detail-header">
        <div className="request-title-section">
          <button
            className={`request-vote-btn ${hasVoted ? 'voted' : ''}`}
            onClick={handleVote}
            title={hasVoted ? 'Remove vote' : 'Vote for this request'}
          >
            <ThumbsUp size={20} />
            <span className="vote-count">{votesCount}</span>
          </button>
          <div className="request-title-content">
            <div className="request-title-row">
              <h1 className="request-title">{request.title}</h1>
              {getAppTypeLabel(request.appType) && (
                <span className={`garage-app-type-badge garage-app-type-${request.appType}`}>
                  {getAppTypeLabel(request.appType)}
                </span>
              )}
            </div>
            <div className="request-meta">
              <span className="request-meta-item">
                <strong>SUBMITTED</strong> · By {request.requesterName || request.requesterEmail}
              </span>
              <span className="request-meta-item">
                {submittedDate || parseTimestamp(request.createdAt).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>
        <div
          className="request-status-badge"
          style={{ '--request-status-color': getStatusColor(request.status) }}
        >
          {getStatusLabel(request.status)}
        </div>
      </div>

      {(request.itInputRequired || itReviewPayload?.itReview?.itInputRequired) && (
        <div
          className="garage-banner garage-banner-warning"
          role="status"
          style={{
            margin: '0 0 1rem',
            padding: '0.85rem 1rem',
            borderRadius: 8,
            background: '#FFF7ED',
            border: '1px solid #FDBA74',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '0.75rem',
            flexWrap: 'wrap',
          }}
        >
          <div>
            <strong>Input required</strong>
            <div style={{ marginTop: 4, color: '#9A3412' }}>
              {(request.requesterEmail || '').toLowerCase() === (userEmail || '').toLowerCase()
                ? 'Provide the repository URL and access token before scans can start.'
                : 'The requestor needs to provide the repository URL and access token before scans can start.'}
            </div>
          </div>
          {(request.requesterEmail || '').toLowerCase() === (userEmail || '').toLowerCase() && (
            <button
              type="button"
              className="garage-btn garage-btn-primary garage-btn-sm"
              onClick={() => setFocusItInitiated(true)}
            >
              Provide details
            </button>
          )}
        </div>
      )}

      {/* Request details (metadata + description) */}
      <div className="request-section">
        <h3 className="request-section-title">Request Details</h3>
        <div className="request-details-grid">
          <div className="request-detail-item">
            <div className="request-detail-label">
              <User size={14} /> TARGET PERSONAS
            </div>
            <div className="request-detail-value">{request.targetPersonas || 'Not specified'}</div>
          </div>
          <div className="request-detail-item">
            <div className="request-detail-label">
              <TrendingUp size={14} /> EXPECTED REACH
            </div>
            <div className="request-detail-value">{request.reach || 'Not specified'}</div>
          </div>
          <div className="request-detail-item">
            <div className="request-detail-label">
              <Clock size={14} /> FREQUENCY
            </div>
            <div className="request-detail-value">{request.frequency || 'Not specified'}</div>
          </div>
          <div className="request-detail-item">
            <div className="request-detail-label">
              <AlertCircle size={14} /> BUSINESS VALUE
            </div>
            <div className="request-detail-value">{request.valueDriver || 'Not specified'}</div>
          </div>
        </div>
        <div className="request-description" style={{ marginTop: '1rem' }}>
          {request.description}
        </div>
      </div>

      <GarageLoadingRegion
        loading={statusTransitioning}
        message={transitionMessage}
        compact
      >
        <RequestWorkflowPanel
          request={request}
          requestId={requestId}
          activity={activity}
          attachments={attachments}
          itReviewPayload={itReviewPayload}
          submittedDate={submittedDate}
          canManageRequests={canManageRequests}
          canEscalateToDot={canEscalateToDot}
          canPublishGem={canPublishGem}
          isGemPublished={isGemPublished}
          userEmail={userEmail}
          userName={userName}
          userRole={userRole}
          downloadingAttachment={downloadingAttachment}
          onDownloadAttachment={handleDownloadAttachment}
          onDeleteAttachment={handleDeleteAttachment}
          onStatusChange={handleStatusChange}
          onRequestInfo={handleRequestInfo}
          onRouteToIT={handleRouteToIT}
          onEscalateToDot={handleEscalateToDot}
          onPublishGem={handlePublishGem}
          actionsDisabled={statusTransitioning}
          focusItInitiated={focusItInitiated}
          onRequestRefresh={async () => {
            await Promise.all([
              loadRequestDetails({ showLoader: false }),
              loadActivity(),
            ]);
          }}
        />
      </GarageLoadingRegion>

      {/* Activity & Comments Section — status change trace + comments */}
      <div className="request-section request-comments-section">
        <h3 className="request-section-title">
          <MessageCircle size={18} /> Status history &amp; activity ({activity.length})
        </h3>
        <p className="request-activity-lede">
          Trace of every status change (who moved it, when) and any comments tied to those steps.
        </p>

        <RequestActivityFeed
          entries={activity}
          TimestampDisplay={TimestampDisplay}
          userEmail={userEmail}
        />

        {/* Add Comment Input */}
        <div className="comment-input-container">
          <RequestAttachmentPicker
            files={attachmentFiles}
            onChange={setAttachmentFiles}
            disabled={submittingComment}
          >
            <textarea
              className="comment-input"
              placeholder="Add a comment or attach supporting files..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              rows={3}
              disabled={submittingComment}
            />
          </RequestAttachmentPicker>
          <div className="comment-input-actions">
            <button
              className="garage-btn garage-btn-primary"
              onClick={handlePostComment}
              disabled={(!newComment.trim() && attachmentFiles.length === 0) || submittingComment}
            >
              <Send size={16} />
              {submittingComment
                ? 'Adding update...'
                : attachmentFiles.length > 0
                  ? 'Add update'
                  : 'Post Comment'}
            </button>
          </div>
        </div>
      </div>

      {/* Admin/Reviewer Modals */}
      {canManageRequests && request && (
        <>
          <StageTransitionModal
            isOpen={stageTransitionModal.isOpen}
            ideaId={requestId}
            fromStatus={request.status}
            toStatus={stageTransitionModal.toStatus}
            title={stageTransitionModal.title}
            confirmLabel={stageTransitionModal.confirmLabel}
            variant={stageTransitionModal.variant}
            userEmail={userEmail}
            userName={userName}
            onClose={() =>
              setStageTransitionModal((s) => ({ ...s, isOpen: false }))
            }
            onConfirm={handleStageTransitionConfirm}
          />
          <RequestInfoModal
            isOpen={requestInfoModalOpen}
            onClose={() => setRequestInfoModalOpen(false)}
            idea={request}
            adminEmail={userEmail}
            adminName={userName}
            onSuccess={() => refreshAfterAction('Requesting info…')}
          />
          <RouteToITModal
            isOpen={routeToITModalOpen}
            onClose={() => setRouteToITModalOpen(false)}
            idea={request}
            adminEmail={userEmail}
            adminName={userName}
            onSuccess={() => refreshAfterAction('Routing to IT…')}
          />
          <EscalateToDotModal
            isOpen={escalateDotModalOpen}
            onClose={() => setEscalateDotModalOpen(false)}
            idea={request}
            adminEmail={userEmail}
            adminName={userName}
            onBackground={() => {
              setPendingDotReview(true);
              setEscalateDotModalOpen(false);
            }}
            onSuccess={() => {
              setPendingDotReview(false);
              refreshAfterAction('Escalating to IT BizOps…');
            }}
          />
          <PublishGemModal
            isOpen={publishGemModalOpen}
            onClose={() => setPublishGemModalOpen(false)}
            idea={request}
            adminEmail={userEmail}
            adminName={userName}
            onSuccess={(result) => {
              refreshAfterAction('Publishing…').then(() => {
                setAlertModal({
                  isOpen: true,
                  title: result?.alreadyPublished ? 'Already published' : 'Gem published',
                  message: result?.alreadyPublished
                    ? 'This request was already published to Discover.'
                    : `“${result?.tool?.title || 'Gem'}” is now live in Discover.`,
                  variant: 'info',
                });
              });
            }}
          />
        </>
      )}

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
      />

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        title={alertModal.title}
        message={alertModal.message}
        variant={alertModal.variant}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
      />
    </div>
  );
};

export default GarageRequestDetailView;
